import React, { useEffect, useState, useRef, useMemo } from 'react';
import apiService from '../../services/apiService';

// Cache directory users globally so we only fetch once per session
let _directoryCache = null;
let _directoryLoading = false;
let _directoryCallbacks = [];

export function loadDirectory() {
    if (_directoryCache) return Promise.resolve(_directoryCache);
    if (_directoryLoading) {
        return new Promise(resolve => _directoryCallbacks.push(resolve));
    }
    _directoryLoading = true;
    return apiService.getDirectoryUsers()
        .then(data => {
            _directoryCache = data.users || [];
            _directoryCallbacks.forEach(cb => cb(_directoryCache));
            _directoryCallbacks = [];
            return _directoryCache;
        })
        .catch(() => {
            _directoryCache = [];
            _directoryCallbacks.forEach(cb => cb([]));
            _directoryCallbacks = [];
            return [];
        })
        .finally(() => { _directoryLoading = false; });
}

/**
 * Type-to-search user selector with SAG directory integration.
 *
 * Loads all SAG directory users once (cached in Redis 24h + in-memory per session).
 * Filters client-side as you type — instant results.
 */
function UserSearch({ users, value, onChange, activities, placeholder, variant, emptyLabel, allLabel }) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [dirUsers, setDirUsers] = useState(_directoryCache || []);
    const wrapperRef = useRef(null);

    // Load directory users once
    useEffect(() => {
        loadDirectory().then(setDirUsers);
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Build frequency map from activities
    const freqMap = useMemo(() => {
        const map = {};
        (activities || []).forEach(a => {
            if (a.assigned_to) {
                map[a.assigned_to] = (map[a.assigned_to] || 0) + 1;
            }
        });
        return map;
    }, [activities]);

    // Merge directory users with logged-in users, dedupe by email
    const allUsers = useMemo(() => {
        const emailSet = new Set();
        const merged = [];

        // Add directory users (have name + email)
        (dirUsers || []).forEach(u => {
            const email = u.email || u;
            if (!emailSet.has(email) && !email.includes('disable-auth')) {
                emailSet.add(email);
                merged.push({ name: u.name || email.split('@')[0], email });
            }
        });

        // Add logged-in users not in directory
        (users || []).forEach(u => {
            const email = typeof u === 'string' ? u : u.email;
            if (!emailSet.has(email) && !email.includes('disable-auth')) {
                emailSet.add(email);
                merged.push({ name: email.split('@')[0], email });
            }
        });

        return merged;
    }, [dirUsers, users]);

    // Filter and sort
    const filtered = useMemo(() => {
        const q = query.toLowerCase();
        const matched = q
            ? allUsers.filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
            : allUsers;
        return matched.sort((a, b) => {
            const fa = freqMap[a.email] || 0;
            const fb = freqMap[b.email] || 0;
            if (fb !== fa) return fb - fa;
            return a.name.localeCompare(b.name);
        }).slice(0, 20); // Show max 20 results
    }, [allUsers, query, freqMap]);

    const handleSelect = (email) => {
        onChange(email);
        setQuery('');
        setOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
        setQuery('');
    };

    const noneLabel = allLabel || emptyLabel || 'Unassigned';
    const selectedUser = allUsers.find(u => u.email === value);
    const displayValue = selectedUser ? selectedUser.name : (value ? value.split('@')[0] : '');
    const isInline = variant === 'inline';

    const highlightMatch = (text, q) => {
        if (!q) return text;
        const idx = text.toLowerCase().indexOf(q.toLowerCase());
        if (idx === -1) return text;
        return (
            <>
                {text.slice(0, idx)}
                <span className="user-search-highlight">{text.slice(idx, idx + q.length)}</span>
                {text.slice(idx + q.length)}
            </>
        );
    };

    return (
        <div className={`user-search-wrapper ${isInline ? 'user-search-inline' : ''}`} ref={wrapperRef}>
            <div className={`user-search-input-wrap ${open ? 'user-search-focus' : ''}`}>
                <input
                    type="text"
                    className={`user-search-input ${isInline ? 'user-search-input-inline' : ''}`}
                    value={open ? query : displayValue}
                    onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
                    onFocus={() => { setOpen(true); setQuery(''); }}
                    onKeyDown={(e) => {
                        // Escape closes the list and keeps focus in the field.
                        if (e.key === 'Escape' && open) {
                            e.stopPropagation();
                            setOpen(false);
                            setQuery('');
                        }
                    }}
                    placeholder={placeholder || 'Type to search...'}
                />
                {value && (
                    <button className="user-search-clear" onClick={handleClear} type="button" title="Clear">
                        <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                )}
            </div>
            {open && (
                <div className="user-search-dropdown">
                    <div
                        className={`user-search-option ${!value ? 'user-search-option-selected' : ''}`}
                        onClick={() => handleSelect('')}
                    >
                        <span className="user-search-option-text">{noneLabel}</span>
                        {!value && <span className="user-search-check">&#10003;</span>}
                    </div>
                    {dirUsers.length === 0 && !query && (
                        <div className="user-search-empty">Loading directory...</div>
                    )}
                    {filtered.map(u => (
                        <div
                            key={u.email}
                            className={`user-search-option ${value === u.email ? 'user-search-option-selected' : ''}`}
                            onClick={() => handleSelect(u.email)}
                        >
                            <span className="user-search-option-text">
                                <span className="user-search-name">{highlightMatch(u.name, query)}</span>
                                <span className="user-search-email">{highlightMatch(u.email, query)}</span>
                                {freqMap[u.email] > 0 && <span className="user-search-freq">{freqMap[u.email]}</span>}
                            </span>
                            {value === u.email && <span className="user-search-check">&#10003;</span>}
                        </div>
                    ))}
                    {query && filtered.length === 0 && dirUsers.length > 0 && (
                        <div className="user-search-empty">No users match "{query}"</div>
                    )}
                </div>
            )}
        </div>
    );
}

export default UserSearch;
